import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { styled, Box, Typography, useTheme } from '@mui/material';
import { ReactComponent as DriversIcon } from '../assets/icons/32/user.svg';
import { ReactComponent as TransactionsIcon } from '../assets/icons/32/payment.svg';
import { ReactComponent as DocumentsIcon } from '../assets/icons/32/documents.svg';
import NavButtonLayerIcon from '../assets/icons/nav_button_layer.svg';
import DrivioLogo from '../components/DrivioLogo';
import ProfilePopover from '../components/ProfilePopover';
import NotificationsPopover from '../components/NotificationsPopover';
import ThemeSwitch from '../components/ThemeSwitch';
import DriversScreen from './DriversScreen';
import DriverDetailsScreen from './DriverDetailsScreen';
import TransactionsScreen from './TransactionsScreen';
import DocumentsScreen from './DocumentsScreen';
import UserTransactionsScreen from './UserTransactionsScreen';
import NotificationsScreen from './NotificationsScreen';
import SvgIcon from '../components/SvgIcon';
import { PATH, ROUTE_PATTERN } from '../constants/routes';
import { THEME_MODES } from '../theme';
import { getThemeColors } from '../helpers/themeStyles';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const SideNav = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
  boxSizing: 'border-box',
});

const SideNavItems = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const SideNavButton = styled(Box, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ theme, $active }) => {
  const colors = getThemeColors(theme);
  const isDarkMode = theme.palette.mode === THEME_MODES.DARK;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    gap: 4,
    height: 68,
    width: 120,
    boxSizing: 'border-box',
    borderRadius: 20,
    cursor: 'pointer',
    textTransform: 'uppercase',
    borderColor: 'transparent',
    color: $active ? colors.activeTextOnAccent : theme.palette.text.primary,
    ...($active
      ? {
          backgroundColor: theme.palette.primary.main,
        }
      : isDarkMode
        ? {
            backgroundImage: `url(${NavButtonLayerIcon})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '100% 100%',
            borderColor: theme.palette.divider,
          }
        : {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }),
  };
});

const Content = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  flex: 1,
  backgroundColor: theme.palette.background.default,
}));

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 32,
});

const HeaderActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
});

const ActionIcons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const SideNavText = styled(Typography, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ theme, $active }) => {
  const colors = getThemeColors(theme);

  return {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.1,
    lineHeight: 1.2,
    color: $active ? colors.activeTextOnAccent : colors.navInactiveText,
  };
});

const Main = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = getThemeColors(theme);

  const isTransactionsScreen = location.pathname.startsWith(PATH.TRANSACTIONS);
  const isDocumentsScreen = location.pathname.startsWith(PATH.DOCUMENTS);
  const isDriversScreen = !isTransactionsScreen && !isDocumentsScreen;

  const handleScreenChange = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  return (
    <Container>
      <SideNav>
        <SideNavItems>
          <SideNavButton $active={isDriversScreen} onClick={() => handleScreenChange(PATH.DRIVERS)}>
            <SvgIcon
              icon={DriversIcon}
              size={20}
              color={isDriversScreen ? colors.activeTextOnAccent : colors.navInactiveIcon}
            />
            <SideNavText $active={isDriversScreen}>Chauffeurs</SideNavText>
          </SideNavButton>
          <SideNavButton
            $active={isTransactionsScreen}
            onClick={() => handleScreenChange(PATH.TRANSACTIONS)}
          >
            <SvgIcon
              icon={TransactionsIcon}
              size={20}
              color={isTransactionsScreen ? colors.activeTextOnAccent : colors.navInactiveIcon}
            />
            <SideNavText $active={isTransactionsScreen}>Transactions</SideNavText>
          </SideNavButton>
          <SideNavButton
            $active={isDocumentsScreen}
            onClick={() => handleScreenChange(PATH.DOCUMENTS)}
          >
            <SvgIcon
              icon={DocumentsIcon}
              size={20}
              color={isDocumentsScreen ? colors.activeTextOnAccent : colors.navInactiveIcon}
            />
            <SideNavText $active={isDocumentsScreen}>Documents</SideNavText>
          </SideNavButton>
        </SideNavItems>
      </SideNav>

      <Content>
        <Header>
          <DrivioLogo />

          <HeaderActions>
            <ActionIcons>
              <ThemeSwitch />
              <NotificationsPopover />
              <ProfilePopover />
            </ActionIcons>
          </HeaderActions>
        </Header>

        <Routes>
          <Route path={ROUTE_PATTERN.DRIVERS} element={<DriversScreen />} />
          <Route path={ROUTE_PATTERN.DRIVER_VEHICLE} element={<DriverDetailsScreen />} />
          <Route path={ROUTE_PATTERN.DRIVER_VEHICLES} element={<DriverDetailsScreen />} />
          <Route path={ROUTE_PATTERN.DRIVER_REVENUES} element={<DriverDetailsScreen />} />
          <Route path={ROUTE_PATTERN.DRIVER} element={<DriverDetailsScreen />} />
          <Route path={ROUTE_PATTERN.TRANSACTIONS} element={<TransactionsScreen />} />
          <Route path={ROUTE_PATTERN.DOCUMENTS} element={<DocumentsScreen />} />
          <Route path={ROUTE_PATTERN.USER_TRANSACTIONS} element={<UserTransactionsScreen />} />
          <Route path={ROUTE_PATTERN.NOTIFICATIONS} element={<NotificationsScreen />} />
          <Route path="*" element={<Navigate to={PATH.DRIVERS} replace />} />
        </Routes>
      </Content>
    </Container>
  );
};

export default Main;
